import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-heart-pulse-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-lg font-semibold whitespace-nowrap ${
              scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}>
              {t('siteName')}
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${
                scrolled || !isHome ? 'text-foreground-700' : 'text-background-50/90'
              } ${location.pathname === '/' ? 'text-primary-500' : ''}`}
            >
              {t('navHome')}
            </Link>
            <div className="relative group">
              <button
                className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap flex items-center gap-1 ${
                  scrolled || !isHome ? 'text-foreground-700' : 'text-background-50/90'
                } ${location.pathname.startsWith('/category') ? 'text-primary-500' : ''}`}
              >
                {t('navCategories')}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="bg-background-50 rounded-lg border border-background-200/70 py-2 w-48">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="block px-4 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className={`${cat.icon} mr-2 text-primary-500`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            <Link
              to="/about"
              className={`text-sm font-medium transition-colors hover:text-primary-500 whitespace-nowrap ${
                scrolled || !isHome ? 'text-foreground-700' : 'text-background-50/90'
              } ${location.pathname === '/about' ? 'text-primary-500' : ''}`}
            >
              {t('navAbout')}
            </Link>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${
              scrolled || !isHome ? 'text-foreground-700' : 'text-background-50'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3 space-y-2">
            <Link to="/" className="block py-2 text-sm text-foreground-700 hover:text-primary-500">
              {t('navHome')}
            </Link>
            <div className="py-2 text-sm font-medium text-foreground-500">
              {t('navCategories')}
            </div>
            <div className="pl-3 space-y-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="block py-1.5 text-sm text-foreground-600 hover:text-primary-500"
                >
                  <i className={`${cat.icon} mr-2 text-primary-500 text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link to="/about" className="block py-2 text-sm text-foreground-700 hover:text-primary-500">
              {t('navAbout')}
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}